// AVEN FORK of cleave.js 1.6.0 (latest, unmaintained). Its Util.getPrefixStrippedValue reverts ANY
// one-shot value that doesn't start with the mask prefix - so a prefixed field (FormFieldSsn's
// "XXX-XX-" last-4) cannot be bulk-cleared or re-filled: .clear(), fill(''), select-all+delete,
// paste, setRawValue(''), and programmatic v-model all snap back to the prior digits; only
// char-by-char editing works. Upstream is frozen with this open (issues #487/#455/#389).
//
// We keep the prefix in the mask and fix it at the source: wrap getPrefixStrippedValue so a
// whole-field replace/clear is accepted instead of reverted, while an in-place prefix edit still
// gets cleave's original protection. Only the leading-prefix, non-numeral path is touched, so
// numeral (%/currency), date and no-prefix masks never reach this branch.

type GetPrefixStrippedValue = (
    value: string,
    prefix: string,
    prefixLength: number,
    prevResult: string,
    delimiter: string,
    delimiters: string[],
    noImmediatePrefix: boolean,
    tailPrefix: boolean,
    signBeforePrefix: boolean,
) => string

export interface CleaveUtil {
    getPrefixStrippedValue?: GetPrefixStrippedValue
    stripDelimiters?: (value: string, delimiter: string, delimiters: string[]) => string
}

export interface CleaveWithUtil {
    Util?: CleaveUtil
}

export function applyCleavePrefixClearFix(Cleave: CleaveWithUtil): void {
    const util = Cleave.Util
    const originalGetPrefixStrippedValue = util && util.getPrefixStrippedValue
    // Only install when the original exists (safe if cleave is mocked/absent at import time).
    if (!util || !originalGetPrefixStrippedValue) {
        return
    }

    util.getPrefixStrippedValue = (value, prefix, prefixLength, prevResult, delimiter, delimiters, noImmediatePrefix, tailPrefix, signBeforePrefix) => {
        const replacesOrClearsWholeField =
            typeof value === 'string' &&
            typeof prevResult === 'string' &&
            typeof prefix === 'string' &&
            prefixLength > 0 &&
            !tailPrefix &&
            !signBeforePrefix &&
            value.slice(0, prefixLength) !== prefix &&
            prevResult.slice(0, prefixLength) === prefix
        if (replacesOrClearsWholeField) {
            // An in-place prefix edit keeps the existing raw digits at the end AND still carries a
            // prefix character; a whole-field replace/clear does neither. Requiring both avoids
            // reverting a legit replacement whose new digits happen to end with the old ones, and
            // avoids treating a stray prefix-shared char in a paste (e.g. a hyphen) as a prefix edit.
            // Slice off the prefix by length BEFORE stripping delimiters, so a prefix that itself
            // contains a delimiter char can't shorten the slice.
            const prevRawValueWithDelimiters = prevResult.slice(prefixLength)
            const prevRawValue = typeof util.stripDelimiters === 'function' ? util.stripDelimiters(prevRawValueWithDelimiters, delimiter, delimiters) : prevRawValueWithDelimiters
            let containsPrefixChar = false
            for (const char of value) {
                if (prefix.includes(char)) {
                    containsPrefixChar = true
                    break
                }
            }
            const isInPlacePrefixEdit = containsPrefixChar && prevRawValue.length > 0 && value.endsWith(prevRawValue)
            if (!isInPlacePrefixEdit) {
                return value
            }
        }
        // Delegate with `this` bound to Util: cleave's getPrefixStrippedValue/stripDelimiters use it.
        return originalGetPrefixStrippedValue.call(util, value, prefix, prefixLength, prevResult, delimiter, delimiters, noImmediatePrefix, tailPrefix, signBeforePrefix)
    }
}
